import { Link, useLocation } from 'react-router-dom';
import {
  sidebarMenuItems,
  sidebarWithAffiliateMenuItems,
} from '@src/constants/MenuItems';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Button, Menu } from 'antd';
import { CloseCircle, LogoutCurve } from 'iconsax-react';
import { Logout } from '@src/pages/Dashboard';
import { ResponsiveModal } from '@src/components/ResponsiveModal';
import clsx from 'clsx';
import { WhiteLabelLogo } from '@src/components';
import { useDispatch, useSelector } from 'react-redux';
import { setProfile } from '@src/store/profile/profileSlice';
import { useLazyGetUserProfileQuery } from '@src/services/profile/profileApiSlice';
import { selectCurrentUser } from '@src/store/user/userSlice';

const STORAGE_KEY = 'selectedSidebarItem';

type SidebarProps = {
  onClose?: () => void;
};

export function Sidebar({ onClose }: SidebarProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const currentUser = useSelector(selectCurrentUser);
  const [selectedItem, setSelectedItem] = useState(() => {
    return localStorage.getItem(STORAGE_KEY) ?? '';
  });

  const [logoutModal, setLogoutModal] = useState(false);

  const [getProfile, { data: profile }] = useLazyGetUserProfileQuery();

  useEffect(() => {
    if (currentUser) {
      getProfile();
    }
  }, [currentUser, getProfile]);

  useEffect(() => {
    if (profile) {
      dispatch(setProfile(profile));
    }
  }, [profile, dispatch]);

  const itemsToUse = profile?.isAffiliate
    ? sidebarWithAffiliateMenuItems
    : sidebarMenuItems;

  const sidebarItems = itemsToUse.map((item) => ({
    key: item.key,
    icon: item.icon,
    label: <Link to={item.url}>{item.label}</Link>,
  }));

  useEffect(() => {
    const lastPathSegment = location.pathname.split('/').filter(Boolean).pop();
    const matchedItem = sidebarMenuItems.find(
      (item) => item.url.split('/').filter(Boolean).pop() === lastPathSegment
    );

    if (lastPathSegment === 'packages') {
      setSelectedItem('store');
    } else {
      const matchedKey = matchedItem?.key ?? '';
      setSelectedItem(matchedKey);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (selectedItem) {
      localStorage.setItem(STORAGE_KEY, selectedItem);
    }
  }, [selectedItem]);

  const handleCloseLogOutModal = () => {
    setTimeout(() => {
      setLogoutModal(false);
    }, 300);
  };

  return (
    <>
      <div className='w-full h-full overflow-auto flex flex-col bg-wl-background'>
        <div className='flex items-center justify-between px-8 mt-8 text-xl font-bold '>
          <div className='flex items-center space-x-1'>
            <WhiteLabelLogo />
          </div>
          <CloseCircle
            className='md:hidden text-wl-on-background'
            size={32}
            variant='Bulk'
            onClick={onClose}
          />
        </div>
        <div className='flex-1 flex items-center justify-center'>
          <Menu
            onClick={(value) => {
              setSelectedItem(value.key);
              onClose?.();
            }}
            selectedKeys={[selectedItem]}
            mode='inline'
            items={sidebarItems}
            className={clsx(
              '!border-none grow bg-transparent px-6 [&_li]:!w-auto [&_li]:!h-auto [&_li]:!leading-none [&_li]:!m-0 [&_li]:!mb-2 [&_li]:!px-5 [&_li]:!py-4',
              '[&_.ant-menu-item]:rounded-2xl [&_.ant-menu-item]:text-wl-on-background [&_.ant-menu-item-selected]:!text-white [&_.ant-menu-item-selected]:!bg-black dark:[&_.ant-menu-item-selected]:!bg-white dark:[&_.ant-menu-item-selected]:!text-black',
              'hover:[&_.ant-menu-item:not(.ant-menu-item-selected)]:!bg-wl-background/5 hover:[&_.ant-menu-item:not(.ant-menu-item-selected)]:!text-wl-on-background dark:hover:[&_.ant-menu-item:not(.ant-menu-item-selected)]:!text-wl-on-background dark:hover:[&_.ant-menu-item:not(.ant-menu-item-selected)]:!bg-white/10',
              'dark:hover:[&_.ant-menu-item-selected]:!text-black dark:hover:[&_.ant-menu-item-selected]:!bg-white',
              '[&_.ant-menu-title-content]:!text-M-14 [&_.ant-menu-title-content]:!leading-normal'
            )}
          />
        </div>
        <div className='hidden md:flex justify-center px-6 mb-6'>
          <Button
            className={clsx(
              'justify-start !h-auto w-full !px-5 !py-4 !bg-transparent !rounded-2xl',
              'text-red-600 border-red-600 hover:!text-red-700 hover:!border-red-700'
            )}
            variant='outlined'
            onClick={() => setLogoutModal(true)}
          >
            <LogoutCurve size='24' variant='Bulk' />
            {t('buttons.signOut')}
          </Button>
        </div>
      </div>

      <ResponsiveModal
        isOpen={logoutModal}
        title={t('pages.auth.signOut.title')}
        headerIcon={<LogoutCurve variant='Bulk' />}
        content={<Logout onClose={handleCloseLogOutModal} />}
        onClose={handleCloseLogOutModal}
      />
    </>
  );
}
